import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ActiveFilterPill } from '../../finder/active-filter-pills';
import { ToSelectOptions } from '../../finder/select-options';
import { FormatDayFirstDate, IsoDateOffsetDays } from '../shared/grant-dates';
import { GRANT_ELIGIBILITY_SHORT_LABELS, SelectedShortEligibility, ShortEligibilityLabels, WireEligibilityValues } from './eligibility-labels';
import { ResearchGrantFacets, ResearchGrantFilterInput } from '../../models/research.models';
import { FormatAwardLine } from './grant-card.component';

type GrantArrayFilterKey = 'funderType' | 'grantor' | 'region';

type DatePostedOption = '' | '7' | '14' | '30' | '90';
type ClosingDateOption = '' | '14' | '30' | '90' | '180';
export type AwardOption = '' | 'lte100k' | '100k-500k' | '500k-2m' | 'gt2m' | 'unstated';

const DATE_POSTED_OPTIONS: ReadonlyArray<{ value: DatePostedOption; label: string }> = [
    { value: '', label: 'Any time' },
    { value: '7', label: 'Past 7 days' },
    { value: '14', label: 'Past 14 days' },
    { value: '30', label: 'Past 30 days' },
    { value: '90', label: 'Past 90 days' },
];

const CLOSING_DATE_OPTIONS: ReadonlyArray<{ value: ClosingDateOption; label: string }> = [
    { value: '', label: 'Any closing date' },
    { value: '14', label: 'Within 14 days' },
    { value: '30', label: 'Within 30 days' },
    { value: '90', label: 'Within 90 days' },
    { value: '180', label: 'Within 6 months' },
];

const AWARD_OPTIONS: ReadonlyArray<{ value: AwardOption; label: string }> = [
    { value: '', label: 'Any amount' },
    { value: 'lte100k', label: 'Up to $100K' },
    { value: '100k-500k', label: '$100K to $500K' },
    { value: '500k-2m', label: '$500K to $2M' },
    { value: 'gt2m', label: 'Over $2M' },
    { value: 'unstated', label: 'Amount not stated' },
];

/**
 * Award-amount bucket → the ResearchGrantFilterInput fields it sends. Five
 * mutually exclusive radio buckets (plus "Any amount"), so no bucket needs
 * `includeUnstatedAward` — that field only ever OR-widens an
 * `awardMinUSD`/`awardMaxUSD` RANGE with unstated-amount grants, which isn't
 * a question this radio group ever asks. "Amount not stated" is its own
 * standalone predicate: `awardUnstatedOnly` (R22).
 */
const AWARD_RANGES: Readonly<Record<Exclude<AwardOption, ''>, { min?: number; max?: number; unstatedOnly?: boolean }>> = {
    lte100k: { max: 100_000 },
    '100k-500k': { min: 100_000, max: 500_000 },
    '500k-2m': { min: 500_000, max: 2_000_000 },
    gt2m: { min: 2_000_000 },
    unstated: { unstatedOnly: true },
};

/**
 * UI-question-level active-filter count for the toolbar's `ActiveFilterCount`
 * badge — one bump per answered filter QUESTION, not per underlying wire
 * field. A "Closing date" pick sets both `closingBefore` and `includeRolling`;
 * counting raw fields (as the generic `CountActiveFilters` helper does) would
 * read that as two active filters instead of one.
 */
export function CountActiveGrantFilterGroups(f: ResearchGrantFilterInput): number {
    let count = 0;
    if (f.funderType && f.funderType.length > 0) count++;
    if (f.grantor && f.grantor.length > 0) count++;
    if (f.excludeGrantor && f.excludeGrantor.length > 0) count++;
    if (f.postedAfter) count++;
    if (f.closingBefore || f.closingAfter || f.rollingOnly) count++;
    if (f.awardMinUSD !== undefined && f.awardMinUSD !== null) count++;
    else if (f.awardMaxUSD !== undefined && f.awardMaxUSD !== null) count++;
    else if (f.awardUnstatedOnly) count++;
    if (f.eligibility && f.eligibility.length > 0) count++;
    if (f.region && f.region.length > 0) count++;
    if (f.includeClosed) count++;
    if (f.recommendedOnly) count++;
    return count;
}

/**
 * Whether any filter inside the rail's "More filters" fold is set — award amount, date posted, eligible applicants or State / region
 * (`state.amt || state.posted || elig.length || geo.length`, P/grant-finder.html:1596). The fold opens by itself when so.
 */
export function GrantFoldHasFilters(f: ResearchGrantFilterInput): boolean {
    const award = (f.awardMinUSD !== undefined && f.awardMinUSD !== null) || (f.awardMaxUSD !== undefined && f.awardMaxUSD !== null) || f.awardUnstatedOnly === true;
    return award || !!f.postedAfter || (f.eligibility?.length ?? 0) > 0 || (f.region?.length ?? 0) > 0;
}

/** Reverse lookup for the pill: which award bucket the wire fields spell. */
export function AwardOptionValueFor(f: ResearchGrantFilterInput): AwardOption | null {
    if (f.awardUnstatedOnly) return 'unstated';
    for (const option of AWARD_OPTIONS) {
        if (option.value === '') continue;
        const range = AWARD_RANGES[option.value];
        if (range.unstatedOnly) continue;
        if ((range.min ?? undefined) === (f.awardMinUSD ?? undefined) && (range.max ?? undefined) === (f.awardMaxUSD ?? undefined)) return option.value;
    }
    return null;
}

export function AwardOptionLabelFor(f: ResearchGrantFilterInput): string | null {
    const value = AwardOptionValueFor(f);
    return value === null ? null : AWARD_OPTIONS.find((o) => o.value === value)?.label ?? null;
}

/**
 * Which `ResearchGrantFilterInput` wire fields each "question" spans — the
 * same table the server's query-understanding resolver keys `fromQuery`
 * group names against. Used by `StripQueryGroups` (agu_app_shell) on a fresh
 * submit to drop the PREVIOUS query's extracted groups before re-extracting,
 * and by `withoutQueryGroup` below when a pill for a query-derived group is
 * removed by hand.
 */
export const GRANT_QUERY_GROUP_FIELDS: Readonly<Record<string, ReadonlyArray<keyof ResearchGrantFilterInput>>> = {
    funderType: ['funderType'],
    grantor: ['grantor'],
    excludeGrantor: ['excludeGrantor'],
    postedAfter: ['postedAfter'],
    closing: ['closingBefore', 'closingAfter', 'includeRolling', 'rollingOnly'],
    award: ['awardMinUSD', 'awardMaxUSD', 'includeUnstatedAward', 'awardUnstatedOnly'],
    eligibility: ['eligibility'],
    region: ['region'],
};

/** Maps a pill's `Key` to the `fromQuery` group name it belongs to, for `withoutQueryGroup`. */
const PILL_KEY_GROUP: Readonly<Record<string, string>> = {
    funderType: 'funderType',
    grantor: 'grantor',
    excludeGrantor: 'excludeGrantor',
    postedAfter: 'postedAfter',
    closingBefore: 'closing',
    closingAfter: 'closing',
    rollingOnly: 'closing',
    award: 'award',
    eligibility: 'eligibility',
    region: 'region',
};

/** Drops `pillKey`'s `fromQuery` group name (if any) so a manually-cleared, query-derived filter doesn't survive the next fresh-submit strip as if it were still query-sourced. */
function withoutQueryGroup(f: ResearchGrantFilterInput, pillKey: string): ResearchGrantFilterInput {
    const group = PILL_KEY_GROUP[pillKey];
    if (!group || !f.fromQuery?.length) return f;
    return { ...f, fromQuery: f.fromQuery.filter((g) => g !== group) };
}

/** Award pill value: the rail's bucket label when the wire fields spell one, else a `FormatAwardLine`-style From/Up to/range line for a query-derived amount that lands off the rail. */
export function AwardPillValue(f: ResearchGrantFilterInput): string | null {
    const bucket = AwardOptionLabelFor(f);
    if (bucket) return bucket;
    const floor = f.awardMinUSD ?? null;
    const ceiling = f.awardMaxUSD ?? null;
    if (floor === null && ceiling === null) return null;
    return FormatAwardLine({ awardFloorUSD: floor, awardCeilingUSD: ceiling });
}

/** True when the pill's question was extracted from the typed query (its group is in `fromQuery`). */
function isFromQuery(f: ResearchGrantFilterInput, pillKey: string): boolean {
    const group = PILL_KEY_GROUP[pillKey];
    return group !== undefined && (f.fromQuery ?? []).includes(group);
}

/** The mockup's PILLLABEL (grants.html:897) applied to the wire shape. One pill per answered question. */
export function BuildGrantFilterPills(f: ResearchGrantFilterInput): ActiveFilterPill[] {
    const pills: ActiveFilterPill[] = [];
    if (f.funderType && f.funderType.length > 0) pills.push({ Key: 'funderType', Label: 'Funder', Value: f.funderType.join(', ') });
    if (f.grantor && f.grantor.length > 0) pills.push({ Key: 'grantor', Label: 'Funder', Value: f.grantor.join(', ') });
    if (f.excludeGrantor && f.excludeGrantor.length > 0) pills.push({ Key: 'excludeGrantor', Label: 'Excluding', Value: f.excludeGrantor.join(', ') });
    if (f.postedAfter) pills.push({ Key: 'postedAfter', Label: 'Posted', Value: `after ${FormatDayFirstDate(f.postedAfter)}` });
    // One pill for the whole "closing by/after" question, even when both wire
    // fields are set — `closingBefore` is the pill's Key either way so its
    // removal (below) always finds a case in the switch.
    if (f.closingBefore && f.closingAfter) {
        pills.push({ Key: 'closingBefore', Label: 'Closing within', Value: `after ${FormatDayFirstDate(f.closingAfter)} · by ${FormatDayFirstDate(f.closingBefore)}` });
    } else if (f.closingBefore) {
        pills.push({ Key: 'closingBefore', Label: 'Closing within', Value: `by ${FormatDayFirstDate(f.closingBefore)}` });
    } else if (f.closingAfter) {
        pills.push({ Key: 'closingAfter', Label: 'Closing within', Value: `after ${FormatDayFirstDate(f.closingAfter)}` });
    }
    if (f.rollingOnly) pills.push({ Key: 'rollingOnly', Label: 'Closing within', Value: 'Rolling' });
    const award = AwardPillValue(f);
    if (award) pills.push({ Key: 'award', Label: 'Award', Value: award });
    if (f.eligibility && f.eligibility.length > 0) pills.push({ Key: 'eligibility', Label: 'Eligible', Value: ShortEligibilityLabels(f.eligibility).join(', ') });
    if (f.region && f.region.length > 0) pills.push({ Key: 'region', Label: 'Location', Value: f.region.join(', ') });
    if (f.includeClosed) pills.push({ Key: 'includeClosed', Label: 'Show', Value: 'Closed grants' });
    if (f.recommendedOnly) pills.push({ Key: 'recommendedOnly', Label: 'Show', Value: 'Recommended only' });
    return pills.map((p) => (isFromQuery(f, p.Key) ? { ...p, FromQuery: true } : p));
}

/** Clears exactly the question a pill names, leaving every other filter in place. */
export function RemoveGrantFilterGroup(f: ResearchGrantFilterInput, key: string): ResearchGrantFilterInput {
    const result = ((): ResearchGrantFilterInput => {
        switch (key) {
            case 'funderType': return { ...f, funderType: [] };
            case 'grantor': return { ...f, grantor: [] };
            case 'excludeGrantor': return { ...f, excludeGrantor: [] };
            case 'postedAfter': return { ...f, postedAfter: undefined };
            // One question, one pill removal: closingBefore, closingAfter and
            // rollingOnly all clear the WHOLE closing question — removing
            // whichever pill is showing must not orphan the other wire fields
            // (a leftover `rollingOnly: true` or bare date with no
            // provenance would otherwise survive as a constraint the member
            // never typed and never asked to keep).
            case 'closingBefore':
            case 'closingAfter':
            case 'rollingOnly':
                return { ...f, closingBefore: undefined, closingAfter: undefined, includeRolling: false, rollingOnly: false };
            case 'award': return { ...f, awardMinUSD: undefined, awardMaxUSD: undefined, includeUnstatedAward: false, awardUnstatedOnly: false };
            case 'eligibility': return { ...f, eligibility: [] };
            case 'region': return { ...f, region: [] };
            case 'includeClosed': return { ...f, includeClosed: false };
            case 'recommendedOnly': return { ...f, recommendedOnly: false };
            default: return f;
        }
    })();
    return withoutQueryGroup(result, key);
}

/**
 * Grant Finder's filter panel — projected into `ra-finder-toolbar`'s
 * `[filters]` slot. Two-way binds `Filters` (`ResearchGrantFilterInput`, the
 * wire shape sent to `researchGrantSearch`) plus two client-only toggles that
 * never reach the server (`ShowNewOnly`/`ShowSavedOnly`, filtering the
 * already-fetched list in the page component). `RecommendedOnly` mirrors
 * `filters.recommendedOnly` — a server-side filter on the All grants results
 * (spec G2).
 *
 * The three single-choice groups (Date posted, Closing date, Award amount)
 * track their OWN selection locally rather than inverting it back out of
 * `Filters` on every render — `postedAfter` is a computed ISO date, not a
 * "7/14/30/90" token, so there is nothing to invert. `ngOnChanges` below only
 * resets a group back to "Any" when its wire field disappears (a "Clear
 * all"), which is the one external-reset case that matters.
 */
@Component({
    standalone: false,
    selector: 'ra-grant-filters',
    templateUrl: './grant-filters.component.html',
})
export class GrantFiltersComponent implements OnChanges {
    @Input() Filters: ResearchGrantFilterInput = {};
    @Output() FiltersChange = new EventEmitter<ResearchGrantFilterInput>();
    @Input() Facets: ResearchGrantFacets | null = null;

    /** Drives `agu-rail-fold`'s auto-open (P/grant-finder.html:1596). */
    get FoldHasFilters(): boolean {
        return GrantFoldHasFilters(this.Filters);
    }

    /** Client-only: filters the already-fetched list to `isNew` items. Not sent to the server. */
    @Input() ShowNewOnly = false;
    @Output() ShowNewOnlyChange = new EventEmitter<boolean>();
    /** Client-only: filters the already-fetched list to `itemState.saved` items. Not sent to the server. */
    @Input() ShowSavedOnly = false;
    @Output() ShowSavedOnlyChange = new EventEmitter<boolean>();
    /** Mirrors `Filters.recommendedOnly === true` — a server-side filter on the All grants results (spec G2), not a tab switch. */
    @Input() RecommendedOnly = false;
    @Output() RecommendedOnlyChange = new EventEmitter<boolean>();

    readonly DatePostedSelect = ToSelectOptions(DATE_POSTED_OPTIONS);
    readonly ClosingDateSelect = ToSelectOptions(CLOSING_DATE_OPTIONS);
    readonly AwardSelect = ToSelectOptions(AWARD_OPTIONS);
    readonly EligibilityShortLabels = GRANT_ELIGIBILITY_SHORT_LABELS.map((g) => g.Short);

    DatePostedValue: DatePostedOption = '';
    ClosingDateValue: ClosingDateOption = '';
    AwardValue: AwardOption = '';

    /**
     * Resets a single-choice group back to "Any" only when its wire field has
     * disappeared from `Filters` (the one external-reset case: a "Clear all")
     * — otherwise leaves the local selection alone, since a computed ISO date
     * can't be inverted back into a "7/14/30/90" token to re-derive it.
     */
    ngOnChanges(): void {
        if (!this.Filters.postedAfter) this.DatePostedValue = '';
        if (!this.Filters.closingBefore) this.ClosingDateValue = '';
        // Spec D9: a query constraint sets the rail's own control where one
        // exists. The award amounts ARE invertible — `AwardOptionValueFor` is
        // the same lookup the pill already uses — so an understood
        // `$100K to $500K` selects that bucket instead of leaving the rail
        // reading `Any amount` beside its own pill (Task 15 round 1, B-3).
        // Posted/closing stay one-way: a computed ISO date can't be inverted
        // back into a "7/14/30/90" token.
        this.AwardValue = AwardOptionValueFor(this.Filters) ?? '';
    }

    Selected(key: GrantArrayFilterKey): readonly string[] {
        return this.Filters[key] ?? [];
    }

    OnArrayChange(key: GrantArrayFilterKey, values: string[]): void {
        // Spec D2: unknown-stays widening applies to query-origin constraints
        // only — a manual edit drops that group's `fromQuery` provenance.
        this.Emit(withoutQueryGroup({ ...this.Filters, [key]: values }, key));
    }

    get SelectedEligibilityShort(): string[] {
        return SelectedShortEligibility(this.Filters.eligibility ?? []);
    }

    OnEligibilityChange(shorts: string[]): void {
        // Spec D2: a manual pick on the eligibility rail drops the
        // eligibility group's query provenance.
        this.Emit(withoutQueryGroup({ ...this.Filters, eligibility: WireEligibilityValues(shorts) }, 'eligibility'));
    }

    OnDatePostedSelect(value: string): void {
        if (value === '' || value === '7' || value === '14' || value === '30' || value === '90') this.OnDatePostedChange(value);
    }

    OnClosingDateSelect(value: string): void {
        if (value === '' || value === '14' || value === '30' || value === '90' || value === '180') this.OnClosingDateChange(value);
    }

    OnAwardSelect(value: string): void {
        if (value === '' || value === 'lte100k' || value === '100k-500k' || value === '500k-2m' || value === 'gt2m' || value === 'unstated') this.OnAwardChange(value);
    }

    OnDatePostedChange(value: DatePostedOption): void {
        this.DatePostedValue = value;
        // Spec D2: a manual pick on the date-posted rail drops the
        // postedAfter group's query provenance.
        this.Emit(withoutQueryGroup({ ...this.Filters, postedAfter: value ? IsoDateOffsetDays(-Number(value)) : undefined }, 'postedAfter'));
    }

    OnClosingDateChange(value: ClosingDateOption): void {
        this.ClosingDateValue = value;
        // Spec D2: a manual pick on the closing-date rail drops the closing
        // group's query provenance — the group's rendered pill Key is
        // `closingBefore` either way.
        this.Emit(withoutQueryGroup({
            ...this.Filters,
            closingBefore: value ? IsoDateOffsetDays(Number(value)) : undefined,
            includeRolling: value ? true : false,
        }, 'closingBefore'));
    }

    OnAwardChange(value: AwardOption): void {
        this.AwardValue = value;
        const picked = value ? AWARD_RANGES[value] : {};
        // Spec D2: a manual pick on the award rail drops the award group's
        // query provenance.
        this.Emit(withoutQueryGroup({
            ...this.Filters,
            awardMinUSD: picked.min,
            awardMaxUSD: picked.max,
            // Neither wire field this radio group can produce ever needs
            // includeUnstatedAward — see the AWARD_RANGES doc comment.
            includeUnstatedAward: false,
            awardUnstatedOnly: picked.unstatedOnly ?? false,
        }, 'award'));
    }

    get IncludeClosed(): boolean {
        return this.Filters.includeClosed === true;
    }

    OnIncludeClosedChange(value: boolean): void {
        this.Emit({ ...this.Filters, includeClosed: value });
    }

    private Emit(next: ResearchGrantFilterInput): void {
        this.FiltersChange.emit(next);
    }
}
